import { useState } from "react";
import { Button, Collapse, Empty, Spin, Switch, Tabs, Tooltip } from "antd";
import { useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import ResizableDrawer from "@/components/UI/ResizableDrawer";
import { useScopeParams } from "@/hooks/useScopeParams";
import medicalReviewApi from "@/services/medicalReviewApi";
import NotesTab from "@/views/shared/NotesTab";
import ChangeDiff, { FieldValueChange } from "./ChangeDiff";
import { fieldLabel } from "./fieldLabels";
import ReviewStatusTag, {
  EscalatedTag,
  LateArrivingTag,
  MarkActionLabel,
  SaeTag,
} from "./ReviewStatusTag";
import ReviewAssignControl from "./ReviewAssignControl";
import ReviewContextTab from "./ReviewContextTab";
import type {
  FieldDiff,
  FieldValueDiff,
  ReviewChange,
  ReviewItem,
  ReviewItemDetail,
  ReviewMarkTarget,
} from "./medicalReview.types";

// The participant profile renders each safety domain as its own tab, so a
// review item can open on the record it came from rather than the profile root.
const PROFILE_TAB: Record<string, string> = {
  adverse_events: "adverse-events",
  conmeds: "concomitant-medications",
  labs: "labs",
  ip_administration: "ip-administration",
  protocol_deviations: "protocol-deviations",
  eligibility: "screening",
};

export default function ReviewItemDrawer({
  trialId,
  itemId,
  open,
  onClose,
  onMark,
  onReopen,
  onOpenItem,
  marking,
}: {
  trialId: string;
  itemId: number | null;
  open: boolean;
  onClose: () => void;
  onMark: (target: ReviewMarkTarget) => void;
  onReopen: (target: ReviewMarkTarget) => void;
  onOpenItem: (item: ReviewItem) => void;
  marking: boolean;
}) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["medical-review-item", trialId, itemId],
    queryFn: () => medicalReviewApi.item(trialId, itemId!),
    enabled: open && itemId !== null,
  });
  const item = data?.item;

  return (
    <ResizableDrawer
      headerTitle="Record review"
      headersArray={
        item
          ? [
              {
                key: "SUBJECT",
                value: (
                  <span className="text-white">{item.subject_id ?? "—"}</span>
                ),
              },
              {
                key: "DOMAIN",
                value: <span className="text-white">{item.domain_label}</span>,
              },
              {
                key: "RECORD",
                value: <span className="text-white">{item.entity_id}</span>,
              },
            ]
          : []
      }
      sizeToggle={{
        collapsedWidth: 680,
        expandedWidth: 1150,
        initialWidth: 680,
      }}
      placement="right"
      open={open}
      onClose={onClose}
      closeIcon={null}
      closable={false}
    >
      {open && isLoading && (
        <div className="flex h-64 items-center justify-center">
          <Spin />
        </div>
      )}
      {open && !isLoading && (isError || !data) && (
        <Empty description="Could not load this record." className="my-16" />
      )}
      {open && !isLoading && data && (
        <DrawerBody
          key={itemId ?? "none"}
          trialId={trialId}
          data={data}
          onMark={onMark}
          onReopen={onReopen}
          onOpenItem={onOpenItem}
          marking={marking}
          onClose={onClose}
        />
      )}
    </ResizableDrawer>
  );
}

function DrawerBody({
  trialId,
  data,
  onMark,
  onReopen,
  onOpenItem,
  marking,
  onClose,
}: {
  trialId: string;
  data: ReviewItemDetail;
  onMark: (target: ReviewMarkTarget) => void;
  onReopen: (target: ReviewMarkTarget) => void;
  onOpenItem: (item: ReviewItem) => void;
  marking: boolean;
  onClose: () => void;
}) {
  const [showEmpty, setShowEmpty] = useState(false);
  const navigate = useNavigate();
  const { buildUrl } = useScopeParams();
  const { data: counts } = useQuery({
    queryKey: ["medical-review-counts", trialId],
    queryFn: () => medicalReviewApi.counts(trialId),
  });
  const canAdmin = Boolean(counts?.can_admin);

  const item = data.item;
  const markTarget: ReviewMarkTarget = {
    item_id: item.id,
    material_hash: item.material_hash,
  };
  const profileTab = PROFILE_TAB[item.domain];
  const signedOff = item.status === "reviewed";
  // The mark that put it in that state: the most recent review, which the
  // append-only ledger keeps even after a later reopen.
  const signature = data.marks.find(
    (mark) => mark.action === "reviewed" || mark.action === "bulk_reviewed",
  );
  // A "new record" event carries the record's opening values, not a
  // before/after, so it is not an amendment and cannot render as a diff.
  const amendments = [...data.changes]
    .filter((change) => change.classification !== "new" && change.field_diff)
    .sort((a, b) => b.detected_at.localeCompare(a.detected_at));
  const [latestChange, ...priorChanges] = amendments;
  const latestDiff: FieldDiff = latestChange?.field_diff ?? {};

  const defaultFields = data.record.fields.filter(
    (field) => field.populated || field.name in latestDiff,
  );
  const fields = showEmpty ? data.record.fields : defaultFields;
  const emptyCount = data.record.fields.length - defaultFields.length;
  // A watched field the record surface does not list would otherwise hide its
  // own amendment, which is the one thing this tab must not do.
  const listedNames = new Set(data.record.fields.map((field) => field.name));
  const unlistedChanges = Object.entries(latestDiff).filter(
    ([name]) => !listedNames.has(name),
  );

  const tabItems = [
    {
      key: "record",
      label: "Record",
      children: (
        <div className="flex flex-col gap-2">
          {emptyCount > 0 && (
            <label className="flex items-center justify-end gap-2 text-xs text-[#556C75]">
              <Switch
                size="small"
                checked={showEmpty}
                onChange={setShowEmpty}
              />
              Show {emptyCount} empty {emptyCount === 1 ? "field" : "fields"}
            </label>
          )}
          {!data.record.found && (
            <p className="m-0 text-xs text-[#A31D5C]">
              The underlying record is no longer present in the clinical data.
            </p>
          )}
          {latestChange && <LatestChangeNotice change={latestChange} />}
          <dl className="m-0 flex flex-col">
            {fields.map((field) => (
              <RecordFieldRow
                key={field.name}
                name={field.name}
                value={field.value}
                change={latestDiff[field.name]}
                changedAt={latestChange?.detected_at}
              />
            ))}
            {unlistedChanges.map(([name, change]) => (
              <RecordFieldRow
                key={name}
                name={name}
                value={change.new}
                change={change}
                changedAt={latestChange?.detected_at}
              />
            ))}
          </dl>
          {priorChanges.length > 0 && (
            <Collapse
              ghost
              size="small"
              className="-mx-3"
              items={[
                {
                  key: "prior-changes",
                  label: (
                    <span className="text-xs text-[#556C75]">
                      History of changes ({priorChanges.length})
                    </span>
                  ),
                  children: (
                    <div className="flex flex-col gap-3">
                      {priorChanges.map((change) => (
                        <section
                          key={change.detected_at}
                          className="flex flex-col gap-1"
                        >
                          <span className="text-[11px] text-[#8FA5A0]">
                            {changeKindLabel(change)} ·{" "}
                            {new Date(change.detected_at).toLocaleString()}
                          </span>
                          <ChangeDiff diff={change.field_diff} />
                        </section>
                      ))}
                    </div>
                  ),
                },
              ]}
            />
          )}
        </div>
      ),
    },
  ];

  // The reviewer's judgement usually needs the participant, not just the
  // record: what else was happening to this person around the same dates,
  // without leaving the queue for the full profile.
  if (item.subject_id) {
    tabItems.push({
      key: "context",
      label: "Context",
      children: (
        <ReviewContextTab
          subjectId={item.subject_id}
          currentItemId={item.id}
          onOpenItem={onOpenItem}
        />
      ),
    });
  }

  tabItems.push({
    key: "history",
    label: data.marks.length ? `History (${data.marks.length})` : "History",
    children: <RecordReviewHistory marks={data.marks} />,
  });

  tabItems.push({
    key: "notes",
    label: "Notes",
    children: <NotesTab entityKey={`review-item:${item.id}`} />,
  });

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-wrap items-center gap-2">
        {item.sae_tier && <SaeTag />}
        {item.late_arriving && <LateArrivingTag />}
        {item.assignment_kind === "escalated" && <EscalatedTag />}
        <ReviewStatusTag status={item.status} />
        {item.assigned_to_name && (
          <span className="rounded-full bg-[#EEF3F6] px-2 py-0.5 text-[11px] text-[#2B4752]">
            {assignmentVerb(item.assignment_kind)} {item.assigned_to_name}
          </span>
        )}
        <span className="text-sm text-[#2B4752]">{item.summary}</span>
      </div>

      {signedOff && <SignedOffNotice signature={signature} />}

      <div className="flex flex-wrap gap-2">
        {signedOff ? (
          // A signed record offers exactly three things: take the signature
          // back, say something about it, or read it. Re-signing something
          // already signed would write a second signature over identical
          // content, which says nothing and muddies the audit trail.
          <Tooltip title="Return this record to the queue. Your review stays in the history, and the reopening is recorded next to it.">
            <Button
              loading={marking}
              onClick={() => {
                onReopen(markTarget);
                onClose();
              }}
            >
              Reopen
            </Button>
          </Tooltip>
        ) : (
          <>
            <Button
              type="primary"
              loading={marking}
              onClick={() => {
                onMark(markTarget);
                onClose();
              }}
            >
              Mark reviewed
            </Button>
            <ReviewAssignControl
              trialId={trialId}
              itemIds={[item.id]}
              canAdmin={canAdmin}
            />
          </>
        )}
        {item.subject_id && profileTab && (
          <Button
            onClick={() =>
              navigate(
                buildUrl(`subjects/overview/${item.subject_id}/${profileTab}`),
                { state: { from: "medical-review" } },
              )
            }
          >
            View Participant Profile →
          </Button>
        )}
      </div>

      <Tabs defaultActiveKey="record" size="small" items={tabItems} />
    </div>
  );
}

/**
 * Every act of review on this one record.
 *
 * The screen behind has a Review history tab too, but that one spans every
 * record and every user; this answers "what happened to *this* record", which
 * is the question a reviewer has with the record open in front of them.
 */
function RecordReviewHistory({ marks }: { marks: ReviewItemDetail["marks"] }) {
  if (marks.length === 0) {
    return (
      <Empty
        description="Nobody has acted on this record yet."
        className="my-8"
      />
    );
  }
  return (
    <div className="flex flex-col gap-2">
      {marks.map((mark) => (
        <div
          key={mark.id}
          className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-dashed border-[#E6ECF1] pb-1.5 text-xs"
        >
          <MarkActionLabel action={mark.action} meaning={mark.meaning} />
          <span className="font-medium text-[#2B4752]">{mark.by}</span>
          {mark.role && <span className="text-[#8FA5A0]">{mark.role}</span>}
          <span className="text-[#556C75]">
            {new Date(mark.at).toLocaleString()}
          </span>
          {mark.comment && (
            <span className="w-full text-[#556C75]">{mark.comment}</span>
          )}
        </div>
      ))}
    </div>
  );
}

function SignedOffNotice({
  signature,
}: {
  signature: ReviewItemDetail["marks"][number] | undefined;
}) {
  return (
    <div className="rounded-lg border border-[#CDE6D9] bg-[#F2FAF6] px-3 py-2">
      <span className="text-xs font-medium text-[#2E7D5B]">
        {signature
          ? `Reviewed by ${signature.by}${
              signature.role ? ` · ${signature.role}` : ""
            } on ${new Date(signature.at).toLocaleString()}`
          : "This record has been reviewed."}
      </span>
    </div>
  );
}

function assignmentVerb(kind: ReviewItem["assignment_kind"]): string {
  if (kind === "escalated") return "Escalated to";
  if (kind === "claimed") return "Claimed by";
  return "Assigned to";
}

function changeKindLabel(change: ReviewChange): string {
  return change.classification === "material"
    ? "Material change"
    : "Administrative change";
}

function LatestChangeNotice({ change }: { change: ReviewChange }) {
  const materialCount = Object.values(change.field_diff ?? {}).filter(
    (field) => field?.material,
  ).length;
  const changedCount = Object.keys(change.field_diff ?? {}).length;
  return (
    <p className="m-0 text-xs text-[#556C75]">
      Amended {new Date(change.detected_at).toLocaleString()} — {changedCount}{" "}
      {changedCount === 1 ? "field" : "fields"} changed
      {materialCount > 0 && `, ${materialCount} material`}
      {change.rule_version ? ` · materiality v${change.rule_version}` : ""}.
      Changed fields are marked below.
    </p>
  );
}

function ChangeMarker({
  material,
  changedAt,
}: {
  material: boolean;
  changedAt?: string;
}) {
  const when = changedAt ? ` on ${new Date(changedAt).toLocaleString()}` : "";
  return (
    <Tooltip
      title={
        material
          ? `Material change${when} — this is why the record is back for review.`
          : `Administrative change${when} — recorded, but it does not reopen review.`
      }
    >
      <span
        aria-label={material ? "Material change" : "Administrative change"}
        className={`mt-1.5 inline-block h-1.5 w-1.5 shrink-0 self-start rounded-full ${
          material ? "bg-[#A31D5C]" : "border border-[#8FA5A0] bg-transparent"
        }`}
      />
    </Tooltip>
  );
}

function RecordFieldRow({
  name,
  value,
  change,
  changedAt,
}: {
  name: string;
  value: unknown;
  change: FieldValueDiff | undefined;
  changedAt?: string;
}) {
  return (
    <div
      className={`flex justify-between gap-3 border-b border-dashed py-1 ${
        change ? "border-[#C7DCE6]" : "border-[#E6ECF1]"
      }`}
    >
      <dt className="m-0 flex gap-1.5 text-xs text-[#556C75]">
        {change && (
          <ChangeMarker
            material={Boolean(change.material)}
            changedAt={changedAt}
          />
        )}
        {fieldLabel(name)}
      </dt>
      <dd className="m-0 max-w-[55%] text-right text-xs break-words text-[#2B4752]">
        {change ? (
          <FieldValueChange change={change} align="right" />
        ) : (
          formatValue(value)
        )}
      </dd>
    </div>
  );
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "—";
  return String(value);
}
